import { PayloadAction } from "@reduxjs/toolkit"
import { 
  AddNotificationActionPayload, 
  CheckPendingNotificationActionPayload, 
  ConfirmNotificationDeliveredActionPayload, 
  ConfirmNotificationReadActionPayload, 
  DeleteNotificationActionPayload, 
  DeliverNotificationActionPayload, 
  GetPendingNotificationsActionPayload, 
  AddNotificationsActionPayload, 
  NotificationSliceState, 
  ReadNotificationActionPayload, 
  UpdateNotificationActionPayload,
} from "./types"
import { addNotification } from "./utils";

export const addNotificationReducer = (state: NotificationSliceState, action: PayloadAction<AddNotificationActionPayload>) => {
  const { pendingNotifications: notifications, unreadCount } = addNotification(state, action.payload);
  state.pendingNotifications = [...notifications];
  state.unreadCount = unreadCount;
}

export const addNotificationsReducer = (state: NotificationSliceState, action: PayloadAction<AddNotificationsActionPayload>) => {
  for(const notification of action.payload.notifications) {
    const { pendingNotifications: notifications, unreadCount } = addNotification(state, notification);
    state.pendingNotifications = [...notifications];
    state.unreadCount = unreadCount;
  }
}

export const getPendingNotificationsReducer = (state: NotificationSliceState, action: PayloadAction<GetPendingNotificationsActionPayload>) => {
  state.pendingNotifications = [];
  state.unreadCount = 0;

  for(const notification of action.payload.notifications) {
    const { pendingNotifications, unreadCount } = addNotification(state, notification);
    state.pendingNotifications = [...pendingNotifications];
    state.unreadCount = unreadCount;
  }
  state.pendingNotificationsUpdateTimestamp = Date.now();
}

export const deleteNotificationReducer = (state: NotificationSliceState, action: PayloadAction<DeleteNotificationActionPayload>) => {
  const notificationIndex = state.pendingNotifications.findIndex((notification) => notification.id === action.payload.id);
  if (notificationIndex < 0) return;
  
  const notification = state.pendingNotifications[notificationIndex];
  const notifications = state.pendingNotifications.splice(notificationIndex, 1);

  state.pendingNotifications = [...notifications];

  if (!notification.read && state.unreadCount > 0) {
    state.unreadCount = state.unreadCount - 1;
  }
}

export const deleteAllNotificationsReducer = (state: NotificationSliceState) => {
  state.pendingNotifications = [];
  state.unreadCount = 0;
  state.pendingNotificationsUpdateTimestamp = undefined;
}

export const checkPendingNotificationReducer = (state: NotificationSliceState, action: PayloadAction<CheckPendingNotificationActionPayload>) => {
  const notificationIndex = state.pendingNotifications.findIndex((notification) => notification.id === action.payload.id);
  if (notificationIndex < 0) return;
  
  const notification = state.pendingNotifications[notificationIndex];
  if (notification.read && notification.delivered) {
    state.pendingNotifications.splice(notificationIndex, 1);
  }
}

export const updateNotificationReducer = (state: NotificationSliceState, action: PayloadAction<UpdateNotificationActionPayload>) => {
  const notificationIndex = state.pendingNotifications.findIndex((notification) => notification.id === action.payload.id);
  if (notificationIndex < 0) return;
  
  const notification = state.pendingNotifications[notificationIndex];
  const notifications = state.pendingNotifications.splice(notificationIndex, 1, {
    ...notification,
    type: action.payload.type,
    createdAt: action.payload.createdAt,
    data: action.payload.data,
    read: action.payload.read ?? notification.read,
    delivered: action.payload.delivered ?? notification.delivered,
  });

  state.pendingNotifications = [...notifications];
}

export const markAsReadReducer = (state: NotificationSliceState, action: PayloadAction<ReadNotificationActionPayload>) => {
  const notificationIndex = state.pendingNotifications.findIndex((notification) => notification.id === action.payload.id);
  if (notificationIndex < 0) return;
  
  const notification = state.pendingNotifications[notificationIndex];
  state.pendingNotifications.splice(notificationIndex, 1, {
    ...notification,
    read: true,
  });
  state.unreadCount = state.pendingNotifications.reduce((count, notification) => {
    return !notification.read ? count + 1 : count;
  }, 0);
}

export const markAsDeliveredReducer = (state: NotificationSliceState, action: PayloadAction<DeliverNotificationActionPayload>) => {
  const notificationIndex = state.pendingNotifications.findIndex((notification) => notification.id === action.payload.id);
  if (notificationIndex < 0) return;
  
  const notification = state.pendingNotifications[notificationIndex];
  state.pendingNotifications.splice(notificationIndex, 1, {
    ...notification,
    delivered: true,
  });
}

export const confirmNotificationDeliveredReducer = (state: NotificationSliceState, action: PayloadAction<ConfirmNotificationDeliveredActionPayload>) => {
  const notificationIndex = state.pendingNotifications.findIndex((notification) => notification.id === action.payload.id);
  if (notificationIndex < 0) return;
  
  const notification = state.pendingNotifications[notificationIndex];
  state.pendingNotifications.splice(notificationIndex, 1, {
    ...notification,
    delivered: true,
    deliveredAt: Date.now(),
  });

  checkPendingNotificationReducer(state, action);
}

export const confirmNotificationReadReducer = (state: NotificationSliceState, action: PayloadAction<ConfirmNotificationReadActionPayload>) => {
  const notificationIndex = state.pendingNotifications.findIndex((notification) => notification.id === action.payload.id);
  if (notificationIndex < 0) return;
  
  const notification = state.pendingNotifications[notificationIndex];
  state.pendingNotifications.splice(notificationIndex, 1, {
    ...notification,
    read: true,
    readAt: Date.now(),
  });

  checkPendingNotificationReducer(state, action);
}
